import React, { useEffect, useState, useCallback } from 'react';
import { Users, UserPlus, Trash2, Loader2, RefreshCw, Eye, EyeOff, Shield } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../utils/api';
import { toast } from 'sonner';

export default function AdminPage() {
  const [users,       setUsers]       = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [creating,    setCreating]    = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [showPwd,     setShowPwd]     = useState(false);
  const [deleteId,    setDeleteId]    = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', password: '', is_admin: false });

  const loadUsers = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data.users || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load users');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setCreating(true);
    try {
      await api.post('/admin/users', form);
      toast.success('Account created for ' + form.email);
      setForm({ full_name: '', email: '', password: '', is_admin: false });
      setShowForm(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm('Delete account for ' + email + '? This cannot be undone.')) return;
    setDeleteId(userId);
    try {
      await api.delete('/admin/users/' + userId);
      toast.success('Account deleted: ' + email);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete user');
    } finally {
      setDeleteId(null);
    }
  };

  const INPUT = "w-full px-3.5 py-2.5 rounded-xl text-sm bg-[#F5F5F7] dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[#1D1D1F] dark:text-slate-100 placeholder:text-[#AEAEB2] focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-150";

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center shadow-sm">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#1D1D1F] dark:text-slate-100">Admin Panel</h1>
              <p className="text-sm text-[#6E6E73] dark:text-slate-400">Manage user accounts</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadUsers} className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-[#F5F5F7] dark:hover:bg-slate-800 text-[#6E6E73] transition-all active:scale-[0.97]" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium shadow-sm active:scale-[0.97] transition-all duration-150">
              <UserPlus className="w-4 h-4" />New user
            </button>
          </div>
        </div>

        {showForm && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
            <h2 className="text-[15px] font-semibold text-[#1D1D1F] dark:text-slate-100 mb-4">Create new account</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">Full name</label>
                  <input className={INPUT} type="text" required value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Ramesh Kumar" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">Email address</label>
                  <input className={INPUT} type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="client@cafirm.com" />
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">Temporary password</label>
                <div className="relative">
                  <input className={INPUT + ' pr-10'} type={showPwd ? 'text' : 'password'} required minLength={8} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 8 characters" />
                  <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E6E73] hover:text-slate-700 transition-colors">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={form.is_admin} onChange={e => setForm(f => ({ ...f, is_admin: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                <span className="text-[13px] text-[#1D1D1F] dark:text-slate-300">Grant admin privileges</span>
              </label>
              <div className="flex items-center gap-3 pt-1">
                <button type="submit" disabled={creating} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50 active:scale-[0.97] transition-all duration-150">
                  {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                  {creating ? 'Creating...' : 'Create account'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setForm({ full_name: '', email: '', password: '', is_admin: false }); }}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm text-[#6E6E73] hover:bg-[#F5F5F7] dark:hover:bg-slate-800 active:scale-[0.97] transition-all duration-150">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <Users className="w-4 h-4 text-[#6E6E73]" />
            <span className="text-[13px] font-semibold text-[#1D1D1F] dark:text-slate-100">
              All users {!loadingList && <span className="font-normal text-[#6E6E73]">({users.length})</span>}
            </span>
          </div>
          {loadingList ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#6E6E73]" /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-8 h-8 text-[#AEAEB2] mx-auto mb-2" />
              <p className="text-sm text-[#6E6E73] dark:text-slate-400">No users yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {users.map((u, i) => (
                <div key={u.id} className={"flex items-center px-6 py-3.5 " + (i % 2 === 1 ? 'bg-[#F9F9FA] dark:bg-slate-800/40' : '')}>
                  <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center text-sky-600 dark:text-sky-400 text-sm font-semibold flex-shrink-0 mr-3">
                    {(u.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1D1D1F] dark:text-slate-100 truncate">{u.full_name || u.email}</p>
                    <p className="text-[12px] text-[#6E6E73] dark:text-slate-400 truncate">{u.email}</p>
                  </div>
                  {u.is_admin && (
                    <span className="ml-3 px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400">Admin</span>
                  )}
                  <p className="ml-4 text-[12px] text-[#AEAEB2] dark:text-slate-500 hidden sm:block flex-shrink-0">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                  </p>
                  <button onClick={() => handleDelete(u.id, u.email)} disabled={deleteId === u.id}
                    className="ml-4 p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 transition-all active:scale-[0.95] flex-shrink-0" title="Delete user">
                    {deleteId === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}